/**
 * VoiceAgentWithRoom - Wrapper component that provides LiveKit room context
 */

import { LiveKitRoom, RoomAudioRenderer, StartAudio, useConnectionState, useLocalParticipant } from '@livekit/components-react'
import { ConnectionState, Room } from 'livekit-client'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import AddFundsModal from '../BillingEnginePage/AddFundsModal'
import { useAgentConnection } from './hooks/useAgentConnection'
import { DEFAULT_CONFIG } from './lib/constants'
import type { VoiceAgentProps } from './lib/types'
import { VoiceAgentPopupInner } from './VoiceAgentPopup'
import { VoiceAgentProvider } from './VoiceAgentProvider'
import { VoiceAgentWebSocket } from './VoiceAgentWebSocket'

/**
 * Inner component that uses LiveKit hooks (must be inside LiveKitRoom)
 */
const VoiceAgentRoomContent: React.FC<VoiceAgentProps & { room: Room; onCallStateChange: (state: any) => void }> = (props) => {
  const { room, onConnect, onDisconnect, onCallStateChange } = props

  // Handle connection state changes
  const connectionState = useConnectionState()
  const { localParticipant } = useLocalParticipant()

  // Memoized callbacks to avoid re-creating on every render
  const handleConnect = useCallback(() => {
    if (connectionState === ConnectionState.Connected) {
      onConnect?.(room)
    }
  }, [connectionState, room, onConnect])

  const handleDisconnect = useCallback(() => {
    if (connectionState === ConnectionState.Disconnected) {
      onDisconnect?.()
    }
  }, [connectionState, onDisconnect])

  useEffect(() => {
    handleConnect()
    handleDisconnect()
  }, [handleConnect, handleDisconnect])

  // Auto-enable microphone when connected
  useEffect(() => {
    if (connectionState === ConnectionState.Connected && localParticipant) {
      localParticipant
        .setMicrophoneEnabled(true, undefined, {
          preConnectBuffer: true,
        })
        .catch((error) => {
          console.error('Failed to enable microphone:', error)
        })
    }
  }, [connectionState, localParticipant])

  return (
    <>
      {React.createElement(RoomAudioRenderer as any)}
      {React.createElement(StartAudio as any, { label: 'Click to enable audio' })}
      <VoiceAgentProvider {...props} onCallStateChange={onCallStateChange}>
        <VoiceAgentPopupInner onClose={props.onClose} />
      </VoiceAgentProvider>
    </>
  )
}

/**
 * Component that manages the LiveKit room connection
 */
const VoiceAgentRoomManager: React.FC<VoiceAgentProps> = (props) => {
  const { onClose } = props
  const config = useMemo(() => ({ ...DEFAULT_CONFIG, ...props }), [props])
  const { connectionDetails, error, isLoading, fetchToken } = useAgentConnection({
    agentId: config.agentId,
    authMode: config.authMode,
    apiKey: config.apiKey,
    apiBaseUrl: config.apiBaseUrl,
    onError: config.onError,
  })

  // State for Add Funds modal (shown on 402 Payment Required errors)
  const [showAddFundsModal, setShowAddFundsModal] = useState(false)
  const [hasUserDismissedModal, setHasUserDismissedModal] = useState(false)

  // Check if this is a payment required error
  const isPaymentRequiredError = error?.message?.includes('402') || error?.message?.toLowerCase().includes('payment required')

  // Auto-open Add Funds modal when payment required error occurs (only once)
  useEffect(() => {
    if (isPaymentRequiredError && !showAddFundsModal && !hasUserDismissedModal) {
      setShowAddFundsModal(true)
    }
  }, [isPaymentRequiredError, showAddFundsModal, hasUserDismissedModal])

  // Handle modal close - also close the voice agent popup to reset parent states
  const handleCloseAddFundsModal = useCallback(() => {
    setShowAddFundsModal(false)
    setHasUserDismissedModal(true)
    // Close the voice agent popup to reset "Testing..." state in parent
    onClose?.()
  }, [onClose])

  const [room] = useState(
    () =>
      new Room({
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: {
          audioPreset: {
            maxBitrate: 64000,
          },
        },
      }),
  )

  // Track whether to connect to the room
  const [shouldConnect, setShouldConnect] = useState(() => {
    const initialValue = config.autoConnect !== false
    console.log('[VoiceAgentWithRoom] Initial shouldConnect:', initialValue, 'autoConnect:', config.autoConnect)
    return initialValue
  })

  // Handle call state changes from VoiceAgentProvider
  const handleCallStateChange = useCallback((state: 'idle' | 'connecting' | 'connected') => {
    console.log('[VoiceAgentWithRoom] handleCallStateChange called with state:', state)
    if (state === 'connecting') {
      console.log('[VoiceAgentWithRoom] Setting shouldConnect to true')
      setShouldConnect(true)
    } else if (state === 'idle') {
      console.log('[VoiceAgentWithRoom] Setting shouldConnect to false')
      setShouldConnect(false)
    }
  }, [])

  // Log connection state for debugging
  useEffect(() => {
    console.log('[VoiceAgentWithRoom] Connection state:', {
      shouldConnect,
      autoConnect: config.autoConnect,
      roomState: room.state,
      hasConnectionDetails: !!connectionDetails,
    })
  }, [shouldConnect, config.autoConnect, room.state, connectionDetails])

  // Show loading state
  if (isLoading) {
    // Render nothing if loading
    return <></>
  }

  // Show error state
  if (error && !connectionDetails) {
    return (
      <>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '20px',
            color: '#EF4444',
          }}
        >
          <div>Failed to connect to voice agent</div>
          <div style={{ fontSize: '14px', marginTop: '8px', color: '#6B7280' }}>{error.message}</div>
          <button
            onClick={fetchToken}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              backgroundColor: '#7C3AED',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>

        <AddFundsModal isOpen={showAddFundsModal} onClose={handleCloseAddFundsModal} message="You've consumed all your credits. Please add funds to your wallet to continue using Resemble." />
      </>
    )
  }

  // No connection details yet
  if (!connectionDetails) {
    // Render nothing if no connection details
    return <></>
  }

  // xAI Realtime backend — render WebSocket agent instead of LiveKit room
  if (connectionDetails.backend === 'xai_realtime' && connectionDetails.bridgeUrl) {
    return (
      <VoiceAgentWebSocket
        agentId={config.agentId}
        agentName={connectionDetails.agentConfig?.name}
        bridgeUrl={connectionDetails.bridgeUrl}
        bridgeType='xai_realtime'
        voiceId={connectionDetails.voiceId}
        instructions={connectionDetails.instructions}
        language={connectionDetails.language}
        onClose={props.onClose}
        onError={props.onError}
        autoConnect={config.autoConnect !== false}
      />
    )
  }

  // Gemini Live backend — render WebSocket agent instead of LiveKit room
  if (connectionDetails.backend === 'gemini_live' && connectionDetails.bridgeUrl) {
    return (
      <VoiceAgentWebSocket
        agentId={config.agentId}
        agentName={connectionDetails.agentConfig?.name}
        bridgeUrl={connectionDetails.bridgeUrl}
        bridgeType='gemini_live'
        voiceId={connectionDetails.voiceId}
        instructions={connectionDetails.instructions}
        onClose={props.onClose}
        onError={props.onError}
        autoConnect={config.autoConnect !== false}
      />
    )
  }

  // Render the LiveKit room with the voice agent
  // Using React.createElement to handle React 17 JSX type compatibility
  return (
    <>
      {React.createElement(
        LiveKitRoom as any,
        {
          room,
          token: connectionDetails.token,
          serverUrl: connectionDetails.serverUrl,
          // Use LiveKitRoom's connect prop for auto-connect, manual control for idle state
          connect: config.autoConnect !== false ? true : shouldConnect,
        },
        React.createElement(VoiceAgentRoomContent, {
          ...props,
          room,
          onCallStateChange: handleCallStateChange,
        }),
      )}
    </>
  )
}

/**
 * Main VoiceAgentWithRoom component
 * Note: LiveKitRoom already provides the room context internally,
 * so we don't need a separate RoomProvider
 */
export const VoiceAgentWithRoom: React.FC<VoiceAgentProps> = (props) => {
  return <VoiceAgentRoomManager {...props} />
}

export default VoiceAgentWithRoom
